import {styled} from 'baseui';
import {Button} from 'baseui/button';
import {Theme} from 'baseui/theme';
import React, {useEffect, useState} from 'react';
import Icon from '@mdi/react';
import {mdiWindowClose} from '@mdi/js';
import ReactCountryFlag from './CountryFlag';
import {
  Currency,
  CURRENCY_SYMBOL_MAP,
  REGION_CURRENCY_FALLBACK,
} from '../constants/countries';
import {useStoreRegionCurrency} from '../utils/hooks';
import {StoreRegionData} from '../utils/storeGeoLocator';

const StyledButton = ($theme: Theme) => ({
  padding: '7px 9px',
  color: $theme.colors.backgroundPrimary,
  background: 'transparent',
  fontWeight: 600,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderRadius: '24px',
  border: '1px solid transparent',
  transition: '500ms ease',
  [$theme.mediaQuery.large]: {
    ':hover': {
      background: 'transparent',
      border: `1px solid ${$theme.colors.accent}`,
    },
  },
  [$theme.mediaQuery.small]: {
    color: $theme.colors.backgroundPrimary,
    padding: '7px 0px',
  },
});

const StyledButtonCurrency = ($theme: Theme) => ({
  padding: '0',
  margin: '0',
  color: $theme.colors.primary,
  background: 'transparent',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  fontWeight: 600,
  ':hover': {
    background: $theme.colors.backgroundPrimary,
  },
});

const StyledButtonIcon = ($theme: Theme) => ({
  color: $theme.colors.primary,
  background: 'transparent',
  fontWeight: 600,
  ':hover': {
    background: $theme.colors.backgroundPrimary,
  },
});

const Wrapper = styled('div', () => ({}));

const SelectMenuWrapper = styled<
  'div',
  {$isActive: boolean; $isStorePage: boolean}
>('div', ({$isActive, $isStorePage, $theme}) => ({
  position: 'absolute',
  top: '58px',
  left: '0',
  width: '100vw',
  height: 'calc(100vh - 58px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: $isActive ? 'auto' : 'none',
  transform: $isActive ? 'scale(1)' : 'scale(0.97)',
  zIndex: $isActive ? 200 : 0,
  opacity: $isActive ? '1' : '0',
  transition: 'all 500ms',
  [$theme.mediaQuery.small]: {
    top: $isStorePage ? '0px' : '-58px',
    height: 'calc(100vh)',
  },
}));

const SelectMenu = styled('div', ({$theme}) => ({
  padding: '31px 26px',
  width: '90%',
  maxWidth: '600px',
  background: $theme.colors.backgroundPrimary,
  fontSize: '25px',
  color: $theme.colors.primary,
  boxShadow: '0px 4px 19px rgba(0, 0, 0, 0.1)',
  borderRadius: '16px',
  [$theme.mediaQuery.small]: {
    top: 'calc(58px + 51px + 50px)',
    width: '90%',
    padding: '20px',
  },
}));

const SelectMenuHeader = styled('div', ({$theme}) => ({
  marginBottom: '50px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${$theme.colors.primary}`,
  fontWeight: 600,
  background: $theme.colors.backgroundPrimary,
  [$theme.mediaQuery.small]: {
    marginBottom: '20px',
  },
}));

const SelectMenuHeaderAction = styled('p', () => ({
  margin: '0',
  padding: '0',
}));

const SelectMenuOptionsWrapper = styled('div', () => ({
  padding: '0',
  margin: '0',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridTemplateRows: '1fr 1fr 1fr 1fr',
  gridAutoFlow: 'column',
  justifyContent: 'space-between',
  gap: '40px 10px',
  alignItems: 'flex-start',
  background: 'transparent',
}));

const Option = styled('div', () => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  height: '24px',
}));

const OptionFlag = styled('div', () => ({
  margin: '0',
  padding: '0',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginRight: '6px',
  width: '15px',
}));

const SelectedOptionName = styled('p', () => ({
  margin: '2px 0 0 4px',
  padding: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'left',
  textAlign: 'left',
}));

const OptionName = styled('p', ({$theme}) => ({
  margin: '0 0 0 6px',
  padding: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  fontSize: '12px',
  color: $theme.colors.contentSecondary,
  alignItems: 'center',
  textAlign: 'left',
  [$theme.mediaQuery.small]: {
    fontSize: '12px',
  },
}));

const OptionRegion = styled('p', ({$theme}) => ({
  margin: '0 0 0 6px',
  padding: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  textAlign: 'left',
  [$theme.mediaQuery.small]: {
    fontSize: '12px',
  },
}));

const TextWrapper = styled('div', () => ({
  display: 'flex',
  flexDirection: 'column',
}));

export interface OptionInterface extends StoreRegionData {
  readonly id?: string | number;
  readonly label?: React.ReactNode;
  readonly disabled?: boolean;
  readonly clearableValue?: boolean;
  readonly isCreatable?: boolean;
}

export interface StateChangeMap {
  select: 'select';
  remove: 'remove';
  clear: 'clear';
}

export interface OnChangeParams {
  value: ReadonlyArray<OptionInterface>;
  option?: OptionInterface;
  type?: StateChangeMap[keyof StateChangeMap];
}
type Props = {
  options: Array<StoreRegionData>;
  isStorePage?: boolean;
  regionChangeTrigger?: string;
};

const formatCurrency = (currency: Currency) => {
  const {prefix, suffix} = CURRENCY_SYMBOL_MAP[currency];
  return `${currency} (${prefix || suffix})`;
};

type FlagProps = {
  region: string;
};

const RenderFlag = React.memo(
  ({region}: FlagProps): JSX.Element => (
    <ReactCountryFlag
      countryCode={region}
      svg
      cdnSuffix='svg'
      style={{margin: 0, padding: 0}}
    />
  )
);

function SelectCurrency({
  options,
  isStorePage = false,
  regionChangeTrigger,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isServerSide, setIsServerSide] = useState(true);
  const [storeCountry, setStoreCountry] = useStoreRegionCurrency();

  const storeCountryWithFallback = storeCountry || REGION_CURRENCY_FALLBACK;

  const handleSelect = (option: StoreRegionData) => {
    formatCurrency(option.currency);
    setIsOpen(!isOpen);
    setStoreCountry([option]);
  };

  useEffect(() => {
    setIsServerSide(false);
  }, []);

  useEffect(() => {
    if (regionChangeTrigger) {
      setIsOpen(true);
    }
  }, [regionChangeTrigger]);

  if (isServerSide) return null;

  return (
    <Wrapper>
      <Button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        overrides={{
          BaseButton: {
            style: ({$theme}) => StyledButton($theme),
          },
        }}
      >
        <OptionFlag>
          <RenderFlag region={storeCountryWithFallback[0].region} />
        </OptionFlag>
        <SelectedOptionName>
          {storeCountryWithFallback[0].currency}
        </SelectedOptionName>
      </Button>

      <SelectMenuWrapper $isActive={isOpen} $isStorePage={isStorePage}>
        <SelectMenu>
          <SelectMenuHeader>
            <SelectMenuHeaderAction>Select your region</SelectMenuHeaderAction>
            <Button
              onClick={() => {
                setIsOpen(!isOpen);
              }}
              overrides={{
                BaseButton: {
                  style: ({$theme}) => StyledButtonIcon($theme),
                },
              }}
            >
              <Icon size={1} path={mdiWindowClose} />
            </Button>
          </SelectMenuHeader>
          <SelectMenuOptionsWrapper>
            {options
              .sort((a, b) => (a.currency < b.currency ? -1 : 1))
              .map((option) => (
                <Button
                  key={option.region}
                  onClick={() => {
                    handleSelect(option);
                  }}
                  overrides={{
                    BaseButton: {
                      style: ({$theme}) => StyledButtonCurrency($theme),
                    },
                  }}
                >
                  <Option>
                    <OptionFlag className='optionFlag'>
                      <RenderFlag region={option.region} />
                    </OptionFlag>
                    <TextWrapper>
                      <OptionRegion>{option.regionName}</OptionRegion>
                      <OptionName>{option.currency}</OptionName>
                    </TextWrapper>
                  </Option>
                </Button>
              ))}
          </SelectMenuOptionsWrapper>
        </SelectMenu>
      </SelectMenuWrapper>
    </Wrapper>
  );
}

export default SelectCurrency;
